<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const tableHeaders = computed(() => [
  t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
]);

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};
</script>

<template>
  <SettingsPageLayout
    :title="$t('CANNED_MGMT.HEADER')"
    :description="$t('CANNED_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
        @click="openAddPopup"
      />
    </template>

    <!-- Enhanced empty state -->
    <div
      v-if="!uiFlags.fetchingList && !records.length"
      class="flex flex-col items-center justify-center gap-4 py-16 px-8 border border-n-weak rounded-xl bg-n-solid-1"
    >
      <span class="i-lucide-message-square-quote size-10 text-n-slate-8" />
      <div class="flex flex-col items-center gap-1.5 max-w-md text-center">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CANNED_MGMT.EMPTY_STATE.TITLE') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ $t('CANNED_MGMT.EMPTY_STATE.DESCRIPTION') }}
        </span>
      </div>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
        blue
        @click="openAddPopup"
      />
    </div>

    <SettingsTable
      v-if="uiFlags.fetchingList || records.length"
      :headers="tableHeaders"
      :is-loading="uiFlags.fetchingList"
      :is-empty="false"
    >
      <tr
        v-for="(cannedItem, index) in records"
        :key="cannedItem.short_code"
        class="hover:bg-n-solid-3 transition-colors"
      >
        <td class="px-5 py-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ cannedItem.short_code }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-normal">
          <span class="text-sm text-n-slate-11 line-clamp-2">
            {{ getPlainText(cannedItem.content) }}
          </span>
        </td>
        <td class="px-5 py-3">
          <div class="flex justify-end gap-1">
            <Button
              v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="openEditPopup(cannedItem)"
            />
            <Button
              v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[cannedItem.id]"
              @click="openDeletePopup(cannedItem, index)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsPageLayout>
</template>
